import {
  Resource_AppointmentRequestStatus,
  Resource_AppointmentStatus,
  useGetPortalUserAppointmentsQuery,
  useListPortalUserAppointmentRequestsQuery,
} from '@/generated/graphql';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import { AppointmentDetails } from '@components/appointment-details';
import { AppointmentRequestDetails } from '@components/appointment-request-details';
import { AppointmentList } from '@components/appointments/appointment-list';
import { AppointmentRequestList } from '@components/appointments/appointment-request-list';
import { AveliosButton } from '@components/common-layout/avelios-button';
import { HeaderProps, PageWrapper } from '@components/page-wrapper';
import { Badge, Card, Col, Row, Tabs } from 'antd';
import Title from 'antd/lib/typography/Title';
import { Maybe } from 'graphql/jsutils/Maybe';
import moment from 'moment';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ScreenSizeContext } from '../_app';
import styles from './appointments.module.scss';

export type AppointmentFilterType = 'upcoming' | 'past' | 'requests';

const Appointments: NextPage = () => {
  const { formatMessage } = useIntl();
  const { isMobile } = useContext(ScreenSizeContext);

  const [appointmentFilter, setAppointmentFilter] = useState<AppointmentFilterType>('upcoming');

  const [maxAppointments, setMaxAppointments] = useState<number>(5);
  const [maxAppointmentRequests, setMaxAppointmentRequests] = useState<number>(5);

  const [selectedAppointmentId, setSelectedAppointmentId] = useState<Maybe<string> | undefined>(
    undefined,
  );
  const [selectedAppointmentRequestId, setSelectedAppointmentRequestId] = useState<
    Maybe<string> | undefined
  >(undefined);

  const router = useRouter();
  const appointmentId = router.query.appointmentId as string;

  const {
    data: appointmentRequestsData,
    loading: requestsLoading,
    error: requestsError,
    refetch: refetchAppointmentRequests,
  } = useListPortalUserAppointmentRequestsQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });

  const filteredAppointmentRequests = useMemo(
    () =>
      appointmentRequestsData?.listPortalUserAppointmentRequests?.proxyAppointmentRequests?.filter(
        (request) =>
          request?.appointmentRequest?.status &&
          ![
            Resource_AppointmentRequestStatus.AppointmentRequestStatusCreated,
            Resource_AppointmentRequestStatus.AppointmentRequestStatusFulfilled,
          ].includes(request.appointmentRequest.status),
      ),
    [appointmentRequestsData],
  );

  const openAppointmentRequestCounter = useMemo(
    () =>
      filteredAppointmentRequests?.filter(
        (proxyRequest) =>
          proxyRequest?.appointmentRequest?.status &&
          ![
            Resource_AppointmentRequestStatus.AppointmentRequestStatusCancelled,
            Resource_AppointmentRequestStatus.AppointmentRequestStatusRejected,
            Resource_AppointmentRequestStatus.AppointmentRequestStatusProposalRejected,
          ].includes(proxyRequest.appointmentRequest.status),
      )?.length || 0,
    [filteredAppointmentRequests],
  );

  const {
    data: appointmentsData,
    loading: appointmentsLoading,
    error: appointmentsError,
    refetch: refetchAppointments,
  } = useGetPortalUserAppointmentsQuery({
    nextFetchPolicy: 'network-only',
    variables: {
      statusNotIn: [
        Resource_AppointmentStatus.AppointmentStatusRequested,
        Resource_AppointmentStatus.AppointmentStatusNotYetPending,
        Resource_AppointmentStatus.AppointmentStatusPending,
      ],
    },
  });

  const upcomingAppointmentsCounter = useMemo(
    () =>
      appointmentsData?.getPortalUserAppointments?.proxyAppointments?.filter(
        (proxyAppointment) =>
          moment(proxyAppointment?.appointment?.startTime?.seconds * 1000).isAfter(moment()) &&
          (appointmentFilter === 'upcoming' ||
            proxyAppointment?.appointment?.appointmentStatus !==
              Resource_AppointmentStatus.AppointmentStatusCancelled) &&
          proxyAppointment?.appointment?.appointmentStatus !==
            Resource_AppointmentStatus.AppointmentStatusCancelled,
      )?.length || 0,
    [appointmentsData, appointmentFilter],
  );

  useEffect(() => {
    refetchAppointments();
    refetchAppointmentRequests();
  }, []);

  const filteredProxyAppointments = appointmentsData?.getPortalUserAppointments?.proxyAppointments
    ?.filter((proxyAppointment) => {
      const startTime = proxyAppointment?.appointment?.startTime;
      if (appointmentFilter === 'upcoming') {
        return startTime?.seconds > moment().unix();
      } else {
        return startTime?.seconds < moment().unix();
      }
    })
    ?.sort((a, b) => a!.appointment!.startTime!.seconds! - b!.appointment!.startTime!.seconds!);

  const sortedProxyAppointmentRequests = filteredAppointmentRequests
    ?.filter(() => true)
    ?.sort((a, b) => {
      const aSeconds = a!.appointmentRequest!.submissionDate?.seconds;
      const bSeconds = b!.appointmentRequest!.submissionDate?.seconds;
      if (aSeconds && bSeconds) {
        return bSeconds - aSeconds;
      } else if (aSeconds && !bSeconds) {
        return 1;
      } else {
        return -1;
      }
    });

  const loadMoreAppointments =
    maxAppointments < (filteredProxyAppointments || [])?.length
      ? () => setMaxAppointments(maxAppointments + 5)
      : undefined;

  const slicedProxyAppointments = filteredProxyAppointments?.slice(0, maxAppointments);

  const loadMoreAppointmentRequests =
    maxAppointmentRequests < (sortedProxyAppointmentRequests || [])?.length
      ? () => setMaxAppointmentRequests(maxAppointmentRequests + 5)
      : undefined;

  const slicedProxyAppointmentRequests = sortedProxyAppointmentRequests?.slice(
    0,
    maxAppointmentRequests,
  );

  const tabs: {
    label: React.ReactNode;
    key: string;
    children: React.ReactNode;
  }[] = [
    {
      label: (
        <span>
          <Badge style={{ backgroundColor: '#133960' }} count={upcomingAppointmentsCounter} />
          &nbsp;
          {formatMessage({ id: 'upcoming' })}
        </span>
      ),
      key: 'upcoming',
      children: (
        <AppointmentList
          appointmentFilter={appointmentFilter}
          loadMore={loadMoreAppointments}
          routedAppointment={appointmentId}
          proxyAppointments={slicedProxyAppointments}
          appointmentId={selectedAppointmentId}
          setAppointmentId={setSelectedAppointmentId}
        />
      ),
    },
    {
      label: (
        <span>
          <Badge
            count={filteredAppointmentRequests?.length ?? 0}
            style={{ backgroundColor: '#133960' }}
          />
          &nbsp;
          {formatMessage({ id: 'requested' })}
        </span>
      ),
      key: 'requests',
      children: (
        <AppointmentRequestList
          loadMore={loadMoreAppointmentRequests}
          appointmentRequestProxies={slicedProxyAppointmentRequests}
          appointmentRequestId={selectedAppointmentRequestId}
          setAppointmentRequestId={setSelectedAppointmentRequestId}
        />
      ),
    },
    {
      label: formatMessage({ id: 'past' }),
      key: 'past',
      children: (
        <AppointmentList
          appointmentFilter={appointmentFilter}
          loadMore={loadMoreAppointments}
          routedAppointment={appointmentId}
          proxyAppointments={slicedProxyAppointments}
          appointmentId={selectedAppointmentId}
          setAppointmentId={setSelectedAppointmentId}
        />
      ),
    },
  ];

  const loading = appointmentsLoading || requestsLoading;
  const error = appointmentFilter === 'requests' ? requestsError : appointmentsError;

  const appointmentClinicId = slicedProxyAppointments?.find(
    (a) => a?.appointment?.id === selectedAppointmentId,
  )?.clinicId;

  const appointmentRequestClinicId = sortedProxyAppointmentRequests?.find(
    (arp) => arp?.appointmentRequest?.id === selectedAppointmentRequestId,
  )?.clinicId;

  const hasMobileSelection =
    isMobile &&
    ((appointmentFilter === 'requests' && selectedAppointmentRequestId) ||
      (appointmentFilter !== 'requests' && selectedAppointmentId));

  const renderDetails = () => (
    <div className={styles.affixed}>
      {hasMobileSelection && (
        <AveliosButton
          icon={<ArrowLeftOutlined />}
          onClick={() => {
            if (appointmentFilter === 'requests') {
              setSelectedAppointmentRequestId(null);
            } else {
              setSelectedAppointmentId(null);
            }
          }}
          type={'link'}
          style={{ marginBottom: 8 }}
        >
          {formatMessage({ id: 'back' })}
        </AveliosButton>
      )}
      {appointmentFilter === 'requests' ? (
        <AppointmentRequestDetails
          clinicId={appointmentRequestClinicId}
          appointmentRequestId={selectedAppointmentRequestId}
        />
      ) : (
        <AppointmentDetails clinicId={appointmentClinicId} appointmentId={selectedAppointmentId} />
      )}
    </div>
  );

  const renderList = () => (
    <Card className={styles.list}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Title>{formatMessage({ id: 'appointments' })}</Title>

        <AveliosButton
          icon={<PlusOutlined />}
          onClick={() => router.push('facility-selection')}
          type={'primary'}
        >
          {formatMessage({ id: 'action.book-appointment' })}
        </AveliosButton>
      </div>
      <Tabs
        items={tabs}
        activeKey={appointmentFilter}
        onChange={(tab) => {
          setAppointmentFilter(tab as AppointmentFilterType);
          setSelectedAppointmentId(null);
          setSelectedAppointmentRequestId(null);
        }}
      />
    </Card>
  );

  return (
    <PageWrapper headerProps={headerProps} contentLoading={loading} error={!!error}>
      <Row gutter={[16, 16]}>
        <Col xs={hasMobileSelection ? 0 : 24} lg={10}>
          {renderList()}
        </Col>
        <Col xs={hasMobileSelection ? 24 : 0} lg={14}>
          {renderDetails()}
        </Col>
      </Row>
    </PageWrapper>
  );
};

export default Appointments;

const headerProps: HeaderProps = {
  title: 'Appointments',
};
